import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    addEdge,
    ReactFlow,
    useNodesState,
    useEdgesState,
    Controls,
    Background,
    MarkerType,
    ConnectionLineType,
    Panel,
    applyNodeChanges,
    applyEdgeChanges
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './CreateWorkflow.css';
import InitialMessageNode from './InitialMessageNode';
import ReplyNode from './ReplyNode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Template from './Template';
import { Drawer } from "@material-tailwind/react";
import { Button } from "@material-tailwind/react";
import { CloseButton, OutlineButton, PrimaryButton } from '../../../components/Buttons';
import debounce from 'lodash/debounce';
import axios from 'axios';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/api/axios';
import ConfirmationModal from '../../../components/ConfirmationModal';
import PreviousTemplates from './PreviousTemplates';
import {
    getCurrentLeadType,
    saveEdgeToLocalStorage,
    loadTemplateFromLocalStorage,
    loadAllTemplatesFromLocalStorage,
    saveTemplateToLocalStorage,
    saveNodePositionToLocalStorage,
    loadNodePositionFromLocalStorage,
    loadNodeTemplateNameFromStorage,
    loadEdgesFromLocalStorage,
    getTemplateType,
    requiresScheduling,
    clearCampaignData,
    hasUnsavedChanges,
    saveCampaignState,
    loadAutoSavedCampaignState,
    clearAutoSavedCampaignState,
    deleteTemplateFromLocalStorage
} from './workflowUtils';
import { Plus } from 'lucide-react';



const nodeTypes = {
    initialMessageNode: InitialMessageNode,
    replyNode: ReplyNode,
};

let nodeIdCounter = 1;

function CreateWorkflow() {
    const { leadType } = useParams();

    const location = useLocation();
    const navigate = useNavigate();
    const { project } = location.state || {};

    const reactFlowWrapper = useRef(null);
    const nodesRef = useRef([]);
    const initialEdgesRef = useRef([]);
    const onCreateTemplateRef = useRef(null);
    const onAddReplyNodeRef = useRef(null);
    const createInitialElementsRef = useRef(null);
    const loadEdgesFromLocalStorageRef = useRef(null);
    const saveEdgeToLocalStorageRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [showTemplateForm, setShowTemplateForm] = useState(false);
    const [templateFormData, setTemplateFormData] = useState(null);
    const [selectedNode, setSelectedNode] = useState(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [isViewMode, setIsViewMode] = useState(false);
    const [defaultEdgeOptions, setDefaultEdgeOptions] = useState({
        type: 'default',
        animated: true,
        style: { stroke: '#2563eb', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#2563eb' },
    });

    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);
    const [existingWorkflows, setExistingWorkflows] = useState(0);
    const [isWorkflowLimitReached, setIsWorkflowLimitReached] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);

    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    const [isLoading, setIsLoading] = useState(false);
    const [scheduledDateTime, setScheduledDateTime] = useState(new Date());
    const [refreshTemplates, setRefreshTemplates] = useState(0);
    const [hasUnsavedChangesState, setHasUnsavedChangesState] = useState(false);
    const [autoSaveInterval, setAutoSaveInterval] = useState(null);

    useEffect(() => {
        if (leadType) {
            localStorage.setItem('current_leadType', leadType);
        }
    }, [leadType]);

    useEffect(() => {
        const currentLeadType = getCurrentLeadType(leadType);
        
      
        const interval = setInterval(() => {
            if (hasUnsavedChangesState) {
                const campaignData = {
                    nodes: nodesRef.current,
                    edges: edges,
                    templates: loadAllTemplatesFromLocalStorage(currentLeadType),
                    timestamp: Date.now()
                };
                saveCampaignState(currentLeadType, campaignData);
            }
        }, 30000); // 30 seconds

        setAutoSaveInterval(interval);

        // Cleanup interval on unmount
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [hasUnsavedChangesState, leadType]);

    // Beforeunload event handler to warn about unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            const currentLeadType = getCurrentLeadType(leadType);
            if (hasUnsavedChanges(currentLeadType)) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
                return 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [leadType]);

    // Check for unsaved changes periodically
    useEffect(() => {
        const checkUnsavedChanges = () => {
            const currentLeadType = getCurrentLeadType(leadType);
            const hasChanges = hasUnsavedChanges(currentLeadType);
            setHasUnsavedChangesState(hasChanges);
        };

        // Check immediately
        checkUnsavedChanges();

        // Set up periodic check
        const interval = setInterval(checkUnsavedChanges, 5000); // Check every 5 seconds

        return () => clearInterval(interval);
    }, [leadType]);

    // Cleanup effect when component unmounts
    useEffect(() => {
        return () => {
            // Clear auto-save data and intervals when component unmounts
            const currentLeadType = getCurrentLeadType(leadType);
            clearAutoSavedCampaignState(currentLeadType);
            
            if (autoSaveInterval) {
                clearInterval(autoSaveInterval);
            }
        };
    }, [leadType, autoSaveInterval]);

    // useEffect(() => {
    //     const checkWorkflowLimit = async () => {
    //         if (!project || !project.projectId) return;

    //         try {
    //             const response = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
    //                 params: {
    //                     customerId: project.customerId,
    //                     projectId: project.projectId
    //                 }
    //             });

    //             const existingWorkflows = response || [];
    //             setExistingWorkflows(existingWorkflows.templates.length)
    //             console.log("existingWorkflows", existingWorkflows)
    //             setIsWorkflowLimitReached(existingWorkflows.templates.length >= 4);

    //         } catch (error) {
    //             console.error("Error checking workflow limit:", error);
    //         }
    //     };

    //     checkWorkflowLimit();
    // }, [project]);

    const handleOpen = useCallback(() => {
       
        setOpen(true);
    }, []);
    const handleClose = useCallback(() => {
   
        setOpen(false);
        setShowTemplateForm(false);
        setTemplateFormData(null);
        setSelectedNode(null);
        setIsViewMode(false); // Reset view mode when closing

        // Reset all nodes to not view-only when closing
        setNodes(nds => nds.map(node => ({
            ...node,
            data: {
                ...node.data,
                isViewOnly: false
            }
        })));
    }, []);


   
    const handleTemplateDelete = useCallback((nodeId) => {
        const currentLeadType = getCurrentLeadType(leadType);
        
      
        const success = deleteTemplateFromLocalStorage(nodeId, currentLeadType);
        
        if (success) {
            toast.success('Template deleted successfully');
            
            // Remove the node from the flow
            setNodes(nds => nds.filter(node => node.id !== nodeId));
            
            // Remove connected edges
            setEdges(eds => eds.filter(edge => edge.source !== nodeId && edge.target !== nodeId));
            
            // Close the template form if it's open for this node
            if (selectedNode && selectedNode.id === nodeId) {
                setSelectedNode(null);
                setShowTemplateForm(false);
                setTemplateFormData(null);
            }
        } else {
            toast.error('Failed to delete template');
        }
    }, [leadType, setNodes, setEdges, selectedNode]);

    const saveEdgeToLocalStorageCallback = useCallback((edge) => {

        const currentLeadType = getCurrentLeadType(leadType);

        saveEdgeToLocalStorage(edge, currentLeadType);
    }, [leadType]);

    useEffect(() => {
        saveEdgeToLocalStorageRef.current = saveEdgeToLocalStorageCallback;
    }, [saveEdgeToLocalStorageCallback]);

    const loadTemplateFromLocalStorageCallback = useCallback((nodeId) => {
        return loadTemplateFromLocalStorage(nodeId, getCurrentLeadType(leadType));
    }, [leadType]);

    const loadAllTemplatesFromLocalStorageCallback = useCallback(() => {
        return loadAllTemplatesFromLocalStorage(getCurrentLeadType(leadType));
    }, [leadType]);

    const saveTemplateToLocalStorageCallback = useCallback((nodeId, templateData) => {
        saveTemplateToLocalStorage(nodeId, templateData, getCurrentLeadType(leadType));
    }, [leadType]);

    const saveNodePositionToLocalStorageCallback = useCallback((nodeId, position, templateName) => {
        saveNodePositionToLocalStorage(nodeId, position, templateName, getCurrentLeadType(leadType));
    }, [leadType]);

    const loadNodePositionFromLocalStorageCallback = useCallback((nodeId) => {
        return loadNodePositionFromLocalStorage(nodeId, getCurrentLeadType(leadType));
    }, [leadType]);

    const loadNodeTemplateNameFromStorageCallback = useCallback((nodeId) => {
        return loadNodeTemplateNameFromStorage(nodeId, getCurrentLeadType(leadType));
    }, [leadType]);

    const loadEdgesFromLocalStorageCallback = useCallback(() => {

        const currentLeadType = getCurrentLeadType(leadType);

        return loadEdgesFromLocalStorage(currentLeadType);
    }, [leadType]);

    const getTemplateTypeCallback = useCallback((nodeType = null, templateData = null) => {
        return getTemplateType(nodeType, templateData, getCurrentLeadType(leadType));
    }, [leadType]);

    const requiresSchedulingCallback = useCallback(() => {
        return requiresScheduling(getCurrentLeadType(leadType));
    }, [leadType]);

  
    const createInitialElements = useCallback(() => {
        const initialNodes = [];
        initialEdgesRef.current = [];

   
        const currentLeadType = getCurrentLeadType(leadType);
        const autoSavedData = loadAutoSavedCampaignState(currentLeadType);
        
        if (autoSavedData && autoSavedData.nodes && autoSavedData.nodes.length > 0) {
           
           
            initialNodes.push(...autoSavedData.nodes);
            initialEdgesRef.current = autoSavedData.edges || [];
            return initialNodes;
        }

        // First, add the initial message node
        const baseNode = {
            id: 'initialMessage',
            type: 'initialMessageNode',
            data: {
                label: 'Initial Message #1',
                headerType: "text",
                headerText: "",
                headerMedia: null,
                variable: "",
                body: "",
                buttons: [],
                leadType: [currentLeadType], 
                onCreateTemplate: null, 
                onAddReplyNode: null,
                isViewOnly: false, 
                project: project
            },
            position: { x: 700, y: 200 } 
        };

        // Load initial message template if exists
        const storedTemplate = loadTemplateFromLocalStorageCallback('initialMessage');
        // Load stored position for the initial message node
        const storedPosition = loadNodePositionFromLocalStorageCallback('initialMessage');

        initialNodes.push({
            ...baseNode,
            data: {
                ...baseNode.data,
                ...(storedTemplate || {}),
                label: storedTemplate?.templateName ? 'Initial Message: ' + storedTemplate.templateName : 'Initial Message #1'
            },
            position: storedPosition || baseNode.position  // Use stored position or default
        });

        // Load all templates at once and filter efficiently
        const savedTemplates = loadAllTemplatesFromLocalStorageCallback();
        const replyTemplates = savedTemplates.filter(t => t.nodeId.startsWith('reply-'));

        // Sort templates efficiently
        replyTemplates.sort((a, b) => {
            const aNum = parseInt(a.nodeId.split('-')[1] || '0');
            const bNum = parseInt(b.nodeId.split('-')[1] || '0');
            return aNum - bNum;
        });

        // Update the nodeIdCounter to be higher than the highest existing node
        if (replyTemplates.length > 0) {
            const highestId = Math.max(...replyTemplates.map(t => {
                const idNum = parseInt(t.nodeId.split('-')[1] || '0');
                return idNum;
            }));
            nodeIdCounter = highestId + 1;
        }

        // Create nodes for each reply template efficiently
        replyTemplates.forEach((template) => {
            const { nodeId, templateData } = template;

            // Load stored position for this node
            const storedPosition = loadNodePositionFromLocalStorageCallback(nodeId);

            const position = storedPosition || { // Use stored position if available, otherwise default
                x: 300,
                y: 300 // Default position if not stored
            };

            // Create the reply node
            const replyNode = {
                id: nodeId,
                type: 'replyNode',
                data: {
                    ...templateData,
                    label: templateData.templateName
                        ? 'Reply: ' + templateData.friendlyName
                        : `Reply #${nodeId.split('-')[1]}`,
                    onCreateTemplate: null,
                    onAddReplyNode: null,
                    isViewOnly: false, // Default to false for new nodes
                    project: project
                },
                position
            };

            initialNodes.push(replyNode);
        });

        // Load edges efficiently
        if (loadEdgesFromLocalStorageRef.current) {
            initialEdgesRef.current = loadEdgesFromLocalStorageRef.current();
        } else {
            console.warn("loadEdgesFromLocalStorage not available yet");
        }

        return initialNodes;
    }, [loadTemplateFromLocalStorageCallback, loadAllTemplatesFromLocalStorageCallback, loadNodePositionFromLocalStorageCallback, getCurrentLeadType, leadType, project]);

    useEffect(() => {
        createInitialElementsRef.current = createInitialElements;
    }, [createInitialElements]);

    useEffect(() => {

        if (!onCreateTemplateRef.current) {
            onCreateTemplateRef.current = () => console.log("onCreateTemplate called before initialization");
        }
        if (!onAddReplyNodeRef.current) {
            onAddReplyNodeRef.current = () => console.log("onAddReplyNode called before initialization");
        }
        if (!loadEdgesFromLocalStorageRef.current) {
            loadEdgesFromLocalStorageRef.current = () => {

                return [];
            };
        }

    }, []);


    useEffect(() => {

        if (!createInitialElementsRef.current ||
            !onCreateTemplateRef.current ||
            !onAddReplyNodeRef.current ||
            !loadEdgesFromLocalStorageRef.current) {

            return;
        }

       
        const initializeWorkflow = () => {
            try {
                setIsInitializing(true);
                
                // Create initial nodes with proper callback initialization
                const initialNodes = createInitialElementsRef.current();

                const nodesWithCallbacks = initialNodes.map(node => ({
                    ...node,
                    data: {
                        ...node.data,
                        onCreateTemplate: (nodeIdOrData, templateData) => {
                            // Ensure the callback is available before calling
                            if (onCreateTemplateRef.current) {
                                onCreateTemplateRef.current(nodeIdOrData, templateData);
                            } else {
                                console.warn('onCreateTemplate not initialized yet');
                            }
                        },
                        onAddReplyNode: (sourceNodeId, buttonIndex) => {
                            if (onAddReplyNodeRef.current) {
                                onAddReplyNodeRef.current(sourceNodeId, buttonIndex);
                            } else {
                                console.warn('onAddReplyNode not initialized yet');
                            }
                        }
                    }
                }));

                // Set the nodes first
                setNodes(nodesWithCallbacks);

                // Then load and set edges in a separate effect
                const loadEdges = () => {
                    const currentLeadType = getCurrentLeadType(leadType);
                    const savedEdges = loadEdgesFromLocalStorageCallback();

                    if (savedEdges && savedEdges.length > 0) {
                        setEdges(savedEdges);
                    } 
                    
                    
                    // Mark initialization as complete
                    setIsInitializing(false);
                };

                // Use a shorter timeout for better responsiveness
                setTimeout(loadEdges, 50);
            } catch (error) {
                console.error('Error initializing workflow:', error);
                setIsInitializing(false);
            }
        };

        // Defer the initialization to avoid blocking the main thread
        requestAnimationFrame(initializeWorkflow);
    }, [setNodes, setEdges]);

 
    const onCreateTemplate = useCallback((nodeIdOrData, templateData) => {
    
        const isViewOnly = nodesRef.current.some(node => node.data.isViewOnly);
        if (isViewOnly) {
           
            return;
        }


        const nodeId = typeof nodeIdOrData === 'object' ? 'initialMessage' : nodeIdOrData;


        const node = nodesRef.current.find((node) => node.id === nodeId);
        if (!node) {
            console.error("Node not found:", nodeId);
            return;
        }

        // If templateData is provided, use it directly
        if (templateData) {
            // Set view-only mode for saved templates
            const isSavedTemplate = Boolean(templateData.customerWhatsappTemplateId);
           
            if (isSavedTemplate) {
                setIsViewMode(true);
            }

            setTemplateFormData({
                ...templateData,
                isViewingSavedTemplate: isSavedTemplate,
                templateId: templateData.customerWhatsappTemplateId,
                isUpdating: false // Set isUpdating to false when opening the form
            });
            setSelectedNode(node);
            
            setShowTemplateForm(true);
            handleOpen();
            return;
        }

        // If the node has a customerWhatsappTemplateId, use that directly
        if (node.data.customerWhatsappTemplateId) {
            // Set view-only mode for saved templates
            setIsViewMode(true);

            setTemplateFormData({
                ...node.data,
                isViewingSavedTemplate: true,
                templateId: node.data.customerWhatsappTemplateId,
                isUpdating: false // Set isUpdating to false when opening the form
            });
            setSelectedNode(node);
         
            setShowTemplateForm(true);
            handleOpen();
            return;
        }


        const storedTemplate = loadTemplateFromLocalStorageCallback(nodeId);


        const defaultTemplate = {
            templateName: "",
            headerType: "text",
            headerText: "",
            headerMedia: null,
            body: "",
            buttons: [],
                leadType: node.data.leadType || [], // Inherit leadType from node data
                leadStatus: Array.isArray(node.data.leadStatus) ? node.data.leadStatus : (node.data.leadStatus ? [node.data.leadStatus] : []), // Inherit leadStatus from node data
            dummyValues: {},
            variable: "",
            conditions: "",
            conditionString: "",
            whatsappTemplateStatus: "NOT_PUBLISHED",
            templateType: "STANDARD_TEMPLATE",
            isPublished: false,
            isNotificationSent: false,
            order: 0,
            isViewingSavedTemplate: false,
            
            templateId: null,
            isUpdating: false // Set isUpdating to false for new templates
        };



        if (storedTemplate) {
            // Ensure stored template buttons are properly formatted
            const formattedStoredTemplate = {
                ...storedTemplate,
                // Inherit leadType from node data if stored template doesn't have it or it's empty
                leadType: (storedTemplate.leadType && storedTemplate.leadType.length > 0)
                    ? storedTemplate.leadType
                    : (node.data.leadType || []),
                // Inherit leadStatus from node data if stored template doesn't have it
                leadStatus: storedTemplate.leadStatus || (Array.isArray(node.data.leadStatus) ? node.data.leadStatus : (node.data.leadStatus ? [node.data.leadStatus] : [])),
             
                isViewingSavedTemplate: false,
                templateId: null,
                isUpdating: false // Set isUpdating to false for stored templates
            };

            setTemplateFormData(formattedStoredTemplate);
        } else {
            // If this is a reply node, add some default text to distinguish it
            if (nodeId.startsWith('reply-')) {
                const nodeNumber = nodeId.split('-')[1];
                defaultTemplate.templateName = `Reply Template ${nodeNumber}`;
            }
            setTemplateFormData(defaultTemplate);
        }

        setSelectedNode(node);
      
        setShowTemplateForm(true);
        handleOpen();
    }, [loadTemplateFromLocalStorageCallback, handleOpen]);

    // Initialize onCreateTemplate ref immediately
    useEffect(() => {
        onCreateTemplateRef.current = onCreateTemplate;
        
    }, [onCreateTemplate]);


    const onAddReplyNode = useCallback((sourceNodeId, buttonIndex) => {
      
        const isViewOnly = nodesRef.current.some(node => node.data.isViewOnly);
        if (isViewOnly) {
            return; // Don't allow adding reply nodes in view mode
        }

        // Count existing templates
        const existingTemplates = nodesRef.current.filter(node =>
            node.type === 'replyNode' || node.type === 'initialMessageNode'
        ).length;

        // Check if we've reached the template limit
        if (existingTemplates >= 4) {
            toast.error("Maximum limit of 4 templates reached for this workflow");
            return;
        }

        if (!reactFlowInstance) {
            return;
        }

        const sourceNode = nodesRef.current.find(node => node.id === sourceNodeId);
        if (!sourceNode) {
            console.error("Source node not found:", sourceNodeId);
            return;
        }


        const newNodeId = `reply-${nodeIdCounter++}`;


        const currentLeadType = getCurrentLeadType(leadType);


        const position = {
            x: sourceNode.position.x + 100,
            y: sourceNode.position.y + 200,
        };

        // Check if the callback refs are set
        if (!onCreateTemplateRef.current || !onAddReplyNodeRef.current) {
            console.error("Callback refs not set properly:", {
                onCreateTemplateRef: !!onCreateTemplateRef.current,
                onAddReplyNodeRef: !!onAddReplyNodeRef.current
            });
        }

        // Get the current values from the refs
        const createTemplateCallback = onCreateTemplateRef.current;
        const addReplyNodeCallback = onAddReplyNodeRef.current;

        // Get the button text if a specific button index was provided
        let buttonText = "";
        if (buttonIndex !== undefined && sourceNode.data.buttons && sourceNode.data.buttons[buttonIndex]) {
            buttonText = sourceNode.data.buttons[buttonIndex].buttonText || `Button ${buttonIndex + 1}`;
        }


        // Create the new node
        const newNode = {
            id: newNodeId,
            type: 'replyNode',
            data: {
                label: buttonText ? `Reply to: ${buttonText}` : `Reply #${nodeIdCounter}`,
                headerType: "text",
                headerText: "",
                headerMedia: null,
                body: "",
                buttons: [],
                sourceButtonIndex: buttonIndex,
                sourceButtonText: buttonText,
                onCreateTemplate: createTemplateCallback,
                onAddReplyNode: addReplyNodeCallback,
                project: project,
                leadType: sourceNode.data.leadType,
                sendDatetime: sourceNode.data.sendDatetime, // Inherit send date from parent
            },
            position
        };



        // Add the new node
        setNodes(nds => {

            return [...nds, newNode];
        });

        // Determine the source handle ID based on the button index
        let sourceHandleId = undefined;
        if (buttonIndex !== undefined) {
            sourceHandleId = `button-${buttonIndex}`;
        }

        // Create an edge from the source node to the new node
        const newEdge = {
            id: `edge-${currentLeadType}-${sourceNodeId}-${newNodeId}`,
            source: sourceNodeId,
            target: newNodeId,
            sourceHandle: sourceHandleId,
            type: 'default',
            animated: true,
            style: {
                stroke: '#2563eb',
                strokeWidth: 2
            },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: '#2563eb'
            },
            data: {
                buttonIndex
            }
        };



        setEdges(eds => {

            return [...eds, newEdge];
        });

        if (saveEdgeToLocalStorageRef.current) {
            try {

                saveEdgeToLocalStorageRef.current(newEdge);

            } catch (error) {
                console.error("Error saving edge to localStorage:", error);
            }
        } else {
            console.error('saveEdgeToLocalStorageRef is not available');
        }
    }, [reactFlowInstance, setNodes, setEdges, getCurrentLeadType]);

    useEffect(() => {
        onAddReplyNodeRef.current = onAddReplyNode;
    }, [onAddReplyNode]);

    // Custom onNodesChange handler to save node positions to localStorage
    const onNodesChange = useCallback(
        (changes) => {
            changes.forEach(change => {
                if (change.type === 'position' && change.position) {
                    saveNodePositionToLocalStorageCallback(
                        change.id,
                        change.position,
                        loadNodeTemplateNameFromStorageCallback(change.id)
                    );
                }
            });

            setNodes((nds) => {
                return applyNodeChanges(changes, nds);
            });
        },
        [setNodes, saveNodePositionToLocalStorageCallback, loadNodeTemplateNameFromStorageCallback]
    );

    // Custom onEdgesChange handler to save edges to localStorage
    const onEdgesChange = useCallback(
        (changes) => {
            setEdges((eds) => {
                const newEdges = applyEdgeChanges(changes, eds);
                
                // Save new edges to localStorage
                changes.forEach(change => {
                    if (change.type === 'add' && change.item) {
                        saveEdgeToLocalStorageCallback(change.item);
                    }
                });
                
                return newEdges;
            });
        },
        [setEdges, saveEdgeToLocalStorageCallback]
    );

    useEffect(() => {

        if (nodes.length === 0) return;

        setNodes(currentNodes =>
            currentNodes.map(node => ({
                ...node,
                data: {
                    onCreateTemplate: onCreateTemplateRef.current,
                    onAddReplyNode: onAddReplyNodeRef.current,
                    ...node.data
                }
            }))
        );
    }, [onCreateTemplate, onAddReplyNode, setNodes, nodes.length]);




    const onNodeClick = useCallback((event, node) => {
        // Check if we're in view-only mode
        const isViewOnly = nodesRef.current.some(node => node.data.isViewOnly);
        if (isViewOnly) {
            return; // Don't allow node selection in view mode
        }
        setSelectedNode(node);
    }, [setSelectedNode]);

    const onNodesDelete = useCallback((deleted) => {
        setSelectedNode(null);
        const currentLeadType = getCurrentLeadType(leadType);
        const deletedNodeIds = deleted.map(node => node.id);
    
      
        deletedNodeIds.forEach(nodeId => {
            try {
            
                localStorage.removeItem(`template-${currentLeadType}-${nodeId}`);

               
                localStorage.removeItem(`node-position-${currentLeadType}-${nodeId}`);
    
             
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && key.startsWith(`edge-${currentLeadType}-`)) {
                        const edge = JSON.parse(localStorage.getItem(key));
                        if (edge.source === nodeId || edge.target === nodeId) {
                            localStorage.removeItem(key);
                            // Since keys are being removed, it's safer to restart the loop
                            i = -1;
                        }
                    }
                }
            } catch (error) {
                console.error("Error removing node data from localStorage:", error);
            }
        });
    
        // Also update the edges in the React Flow state
        setEdges(eds => eds.filter(edge => !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)));
    
    }, [leadType, setEdges, setSelectedNode]);

    // Debounced version of handleTemplateUpdate
    const debouncedHandleTemplateUpdate = useCallback(
        debounce((templateData) => {
            console.log('debouncedHandleTemplateUpdate - templateData:', templateData);
            console.log('debouncedHandleTemplateUpdate - templateData.leadStatus:', templateData.leadStatus);
            // Update nodes
            setNodes((nds) =>
                nds.map((node) => {
                    if (node.id === selectedNode.id) {
                        // Update node label based on template name if it exists
                        const label = templateData.templateName
                            ? (node.type === 'initialMessageNode'
                                ? 'Initial Message: '
                                : 'Reply: ') + templateData.templateName
                            : node.data.label;

                        // Get current position to save with template name
                        const currentPosition = node.position;

                        // Save position with template name
                        saveNodePositionToLocalStorageCallback(
                            node.id,
                            currentPosition,
                            templateData.templateName
                        );

                        return {
                            ...node,
                            data: {
                                ...node.data,
                                ...templateData,
                                label  // Update the label with the template name
                            },
                            position: { ...node.position }, // Force re-render
                        };

                    }
                    return node;
                })
            );

            // Save to local storage
            if (selectedNode) {
                saveTemplateToLocalStorageCallback(selectedNode.id, templateData);
            }
        }, 300), // 300ms delay
        [selectedNode, setNodes, saveTemplateToLocalStorageCallback, saveNodePositionToLocalStorageCallback]
    );

    const handleTemplateUpdate = useCallback(async (templateData) => {
        console.log('CreateWorkflow handleTemplateUpdate - templateData:', templateData);
        console.log('CreateWorkflow handleTemplateUpdate - templateData.leadStatus:', templateData.leadStatus);
        
        // Set loading state for template updates
        if (templateData.customerWhatsappTemplateId && templateData.isUpdating) {
            setIsUpdatingTemplate(true);
        }

        try {
            // If this is a saved template and we're explicitly updating it
            if (templateData.customerWhatsappTemplateId && templateData.isUpdating) {
                // Check if this is a published template update
                const isPublishedUpdate = templateData.isPublished && 
                    templateData.customerWhatsappTemplateId;

                if (isPublishedUpdate) {
                    // For published templates, send all existing fields but only update specific ones
                    console.log('CreateWorkflow - templateData.leadStatus:', templateData.leadStatus);
                    const finalLeadStatus = Array.isArray(templateData.leadStatus) ? templateData.leadStatus : (templateData.leadStatus ? [templateData.leadStatus] : null);
                    console.log('CreateWorkflow - finalLeadStatus:', finalLeadStatus);
                    const updateData = {
                        customerWhatsappTemplateId: templateData.customerWhatsappTemplateId,
                        friendly_name: templateData.templateName,
                        persona: Array.isArray(templateData.leadType)
                            ? templateData.leadType.map(id => parseInt(id, 10))
                            : [1, 2, 3, 4, 5],
                        lead_status: finalLeadStatus, // Include lead status
                        send_datetime: templateData.sendDatetime || null,
                        // Include all existing fields to preserve them
                        template_type: templateData.templateType || 'STANDARD_TEMPLATE',
                        conditions: templateData.conditions || '',
                        // Include components to preserve existing content
                        components: templateData.components || []
                    };

                    // Add components if they exist
                    if (templateData.components && Array.isArray(templateData.components)) {
                        templateData.components.forEach(component => {
                            updateData.components = updateData.components || [];
                            updateData.components.push(JSON.stringify(component));
                        });
                    }

                    const response = await axiosInstance.put('/customer/whatsapp/template', updateData);

                    if (response) {
                        // Update the node data
                        setNodes(nds => nds.map(node => {
                            if (node.id === selectedNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        templateName: templateData.templateName,
                                        leadType: templateData.leadType,
                                        sendDatetime: templateData.sendDatetime,
                                        customerWhatsappTemplateId: response.data?.customerWhatsappTemplateId || templateData.customerWhatsappTemplateId
                                    }
                                };
                            }
                            return node;
                        }));

                        toast.success('Published template updated successfully');
                        handleClose();
                        return;
                    }
                }

                // Regular template update (full update)
                const formData = new FormData();
                formData.append('customer_id', project.customerId);
                formData.append('project_id', project.projectId);

                // Convert persona to array of integers
                const personaIds = Array.isArray(templateData.leadType)
                    ? templateData.leadType.map(id => parseInt(id, 10))
                    : [1, 2, 3, 4, 5]; // Default values if not provided

                // Append each persona ID separately
                personaIds.forEach(id => {
                    formData.append('persona', id);
                });

                // Add lead_status if it exists (handle both single value and array)
                console.log('CreateWorkflow - templateData.leadStatus for FormData:', templateData.leadStatus);
                if (templateData.leadStatus) {
                    if (Array.isArray(templateData.leadStatus)) {
                        templateData.leadStatus.forEach(status => {
                            console.log('CreateWorkflow - appending lead_status:', status);
                            formData.append('lead_status', status);
                        });
                    } else {
                        console.log('CreateWorkflow - appending single lead_status:', templateData.leadStatus);
                        formData.append('lead_status', templateData.leadStatus);
                    }
                } else {
                    console.log('CreateWorkflow - no leadStatus to append');
                }

                formData.append('friendly_name', templateData.templateName);
                formData.append('conditions', templateData.conditions || '');

                // Add send_datetime if it exists in templateData
                if (templateData.sendDatetime) {
                    formData.append('send_datetime', templateData.sendDatetime);
                }

                // Determine template type based on node type
                let templateType = templateData.templateType || 'STANDARD_TEMPLATE';

                // If this is a reply node, always use REPLY_NODE_TEMPLATE
                if (selectedNode && selectedNode.type === 'replyNode') {
                    templateType = 'REPLY_NODE_TEMPLATE';
                } else {
                    // For non-reply nodes, use the template type from the data or determine it
                    templateType = getTemplateType(selectedNode?.id, templateData);
                }

                formData.append('template_type', templateType);

                // Create components array
                const componentsArray = [];

                // Add header component if exists
                if (templateData.headerType && templateData.headerType !== 'text') {
                    componentsArray.push(JSON.stringify({
                        type: 'HEADER',
                        format: templateData.headerType.toUpperCase(),
                        example: {
                            header_handle: templateData.headerMedia || []
                        }
                    }));
                } else if (templateData.headerText) {
                    componentsArray.push(JSON.stringify({
                        type: 'HEADER',
                        format: 'TEXT',
                        text: templateData.headerText,
                        example: {
                            header_text: [templateData.headerText]
                        }
                    }));
                }

                // Add body component
                if (templateData.body) {
                    componentsArray.push(JSON.stringify({
                        type: 'BODY',
                        text: templateData.body
                    }));
                }

                // Add buttons component if exists
                if (templateData.buttons && templateData.buttons.length > 0) {
                    const buttonComponents = templateData.buttons.map(button => {
                        // Safely determine button type
                        let buttonType = 'QUICK_REPLY'; // default type

                        if (button.typeOfAction === 'call_phone_number' && button.phoneNumber) {
                            buttonType = 'PHONE_NUMBER';
                        } else if ((button.typeOfAction === 'visit_website' || button.typeOfAction === 'site_visit') && button.websiteURL) {
                            buttonType = 'URL';
                        } else if (button.typeOfAction === 'property_config') {
                            buttonType = 'QUICK_REPLY';
                        }
                        else if (button.typeOfAction === 'copy_offer_code') {
                            buttonType = 'COPY_CODE';
                        }

                        return {
                            type: buttonType,
                            text: button.buttonText || button.label || '',
                            ...(buttonType === 'PHONE_NUMBER' && { phone_number: button.phoneNumber }),
                            ...(buttonType === 'URL' && { url: button.websiteURL }),
                            ...(buttonType === 'COPY_CODE' && { offer_code: button.offerCode || button.offer_code })
                        };
                    });
                    componentsArray.push(JSON.stringify({
                        type: 'BUTTONS',
                        buttons: buttonComponents
                    }));
                }

                // Append each component as a separate string
                componentsArray.forEach(component => {
                    formData.append('components', component);
                });

                // Add media if present
                if (templateData.headerMedia?.file) {
                    formData.append('media', templateData.headerMedia.file);
                }

                const response = await axiosInstance.put('/customer/whatsapp/template', formData, {
                    params: {
                        customerWhatsappTemplateId: templateData.customerWhatsappTemplateId,
                        isPublished: templateData.isPublished || false
                    },
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response) {
                    // Update the node data
                    setNodes(nds => nds.map(node => {
                        if (node.id === selectedNode.id) {
                            return {
                                ...node,
                                data: {
                                    ...node.data,
                                    ...templateData,
                                    customerWhatsappTemplateId: response.data?.customerWhatsappTemplateId || templateData.customerWhatsappTemplateId
                                }
                            };
                        }
                        return node;
                    }));

                    // Refresh drip campaign data
                    try {
                        const dripResponse = await axiosInstance.get('/customer/whatsapp/drip-campaign', {
                            params: {
                                customerId: project.customerId,
                                projectId: project.projectId
                            }
                        });

                    } catch (error) {
                        console.error('Error refreshing drip campaign data:', error);
                    }

                    toast.success('Template updated successfully');
                    handleClose();
                }
            } else {
                // Handle local storage updates
                debouncedHandleTemplateUpdate(templateData);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            toast.error('Failed to update template');
        } finally {
            setIsUpdatingTemplate(false);
        }
    }, [project, selectedNode, debouncedHandleTemplateUpdate, handleClose]);


    const onEdgeClick = useCallback((event, edge) => {

        const currentLeadType = getCurrentLeadType(leadType);


        if (window.confirm(`Do you want to delete this connection?`)) {

            setEdges(eds => eds.filter(e => e.id !== edge.id));


            try {
                const edgeKey = `edge-${currentLeadType}-${edge.source}-${edge.target}`;
                localStorage.removeItem(edgeKey);

            } catch (error) {
                console.error("Error removing edge from localStorage:", error);
            }
        }
    }, [setEdges, getCurrentLeadType]);

    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);

    const resetFlow = useCallback(() => {
        setShowResetConfirmation(true);
        setIsViewMode(false);
    }, []);

    const createNewWorkflow = useCallback(() => {
        const currentLeadType = getCurrentLeadType(leadType);

        // Clear all campaign data using the utility function
        const clearedCount = clearCampaignData(currentLeadType);
        clearAutoSavedCampaignState(currentLeadType);
        
       

        // Reset the node counter
        nodeIdCounter = 1;

        // Create a new initial message node (callbacks will be set by useEffect)
        const resetNode = {
            id: 'initialMessage',
            type: 'initialMessageNode',
            data: {
                label: 'Initial Message #1',
                headerType: "text",
                headerText: "",
                headerMedia: null,
                variable: "",
                body: "",
                buttons: [],
                leadType: [currentLeadType], // Set the current lead type for the reset node
                isViewOnly: false, // Default to false for new nodes
                project: project
            },
            position: { x: 700, y: 200 }
        };

        // Reset nodes and edges
        setNodes([resetNode]);
        setEdges([]);
        setIsViewMode(false);
        
        // Reset template form data to clear edit mode
        setTemplateFormData(null);

        // Center the view on the reset node
        setTimeout(() => {
            if (reactFlowInstance) {
                reactFlowInstance.fitView({ padding: 0.2 });
            }
        }, 100);
    }, [getCurrentLeadType, setNodes, setEdges, project, reactFlowInstance]);

    const handleConfirmReset = useCallback(() => {
        const currentLeadType = getCurrentLeadType(leadType);

        // Clear all campaign data using the utility function
        const clearedCount = clearCampaignData(currentLeadType);
        clearAutoSavedCampaignState(currentLeadType);
        
       

        // Reset the node counter
        nodeIdCounter = 1;

        // Create a new initial message node (callbacks will be set by useEffect)
        const resetNode = {
            id: 'initialMessage',
            type: 'initialMessageNode',
            data: {
                label: 'Initial Message #1',
                headerType: "text",
                headerText: "",
                headerMedia: null,
                variable: "",
                body: "",
                buttons: [],
                leadType: [currentLeadType], // Set the current lead type for the reset node
                isViewOnly: false, // Default to false for new nodes
                project: project
            },
            position: { x: 700, y: 200 }
        };

        // Reset nodes and edges
        setNodes([resetNode]);
        setEdges([]);
        setIsViewMode(false);
        
        // Reset template form data to clear edit mode
        setTemplateFormData(null);

        // Center the view on the reset node
        setTimeout(() => {
            if (reactFlowInstance) {
                reactFlowInstance.fitView({ padding: 0.2 });
            }
        }, 100);

        // Close the confirmation modal
        setShowResetConfirmation(false);
    }, [getCurrentLeadType, setNodes, setEdges, project, reactFlowInstance]);

    const handleCancelReset = useCallback(() => {
        setShowResetConfirmation(false);
    }, []);

   
    const getTemplateType = useCallback((nodeType = null, templateData = null) => {
      
        const currentLeadType = getCurrentLeadType(leadType).toLowerCase();
        if (templateData && templateData.buttons) {
            const hasSiteVisitButton = templateData.buttons.some(
                button => button.typeOfAction === 'site_visit'
            );
            if (hasSiteVisitButton) {
               
                return 'SITE_VISIT_TEMPLATE';
            }
        }
        
        if (nodeType && nodeType.startsWith('reply-')) {
            return 'REPLY_NODE_TEMPLATE';
        }

        // Check if template has a site visit button
        if (templateData && templateData.buttons) {
            const hasSiteVisitButton = templateData.buttons.some(button => button.typeOfAction === 'site_visit');
            if (hasSiteVisitButton) {
                return 'SITE_VISIT_TEMPLATE';
            }
        }

        // If the template has conditions, use CAMPAIGN_CONDITIONAL_TEMPLATE
        if (templateData && templateData.conditionString) {
            return 'CAMPAIGN_CONDITIONAL_TEMPLATE';
        }

        // Map lead types to template types
        const templateTypeMap = {
            'birthday_template': 'BIRTHDAY_TEMPLATE',
            'birthday_offer_template': 'BIRTHDAY_OFFER_TEMPLATE',
            'festival_template': 'FESTIVAL_TEMPLATE',
            'festival_offer_template': 'FESTIVAL_OFFER_TEMPLATE',
            'conditional': 'CAMPAIGN_CONDITIONAL_TEMPLATE',
            'post_visit': 'POST_VISIT_TEMPLATE',
            'buildvizer': 'BUILDVIZER_TEMPLATE',
            'standard': 'STANDARD_TEMPLATE'
        };

        // Return the mapped template type or default to STANDARD_TEMPLATE
        return templateTypeMap[currentLeadType] || 'STANDARD_TEMPLATE';
    }, [getCurrentLeadType, leadType]);

    // Function to check if the current lead type requires scheduling
    const requiresScheduling = useCallback(() => {
        const currentLeadType = getCurrentLeadType(leadType).toLowerCase();
        return currentLeadType === 'standard' || currentLeadType === 'birthday';
    }, [getCurrentLeadType]);

    const saveWorkflow = useCallback(async () => {
        setIsLoading(true);
        try {
            if (!project || !project.projectId) {
                toast.error("Project information missing");
                setIsLoading(false);
                return;
            }
    
            const templates = [];
            const replies = [];
            const fileNames = [];
            const files = [];
    
            const allTemplates = loadAllTemplatesFromLocalStorageCallback();
            const allEdges = loadEdgesFromLocalStorageCallback();
            const currentLeadType = getCurrentLeadType(leadType);
            
            allTemplates.sort((a, b) => (a.nodeId === 'initialMessage' ? -1 : (b.nodeId === 'initialMessage' ? 1 : 0)));
    
            for (const item of allTemplates) {
                const { nodeId, templateData } = item;
                console.log(`saveWorkflow - Processing template ${nodeId}:`, templateData);
                console.log(`saveWorkflow - templateData.leadStatus for ${nodeId}:`, templateData.leadStatus);
                const components = [];
    
                // Handle header components (text or media)
                if (templateData.headerType) {
                    if (templateData.headerType.toUpperCase() === "TEXT" && templateData.headerText) {
                        components.push({
                            type: "HEADER",
                            format: "TEXT",
                            text: templateData.headerText || "",
                            example: {
                                header_text: [templateData.headerText || ""]
                            }
                        });
                    } else if (templateData.headerType.toUpperCase() !== "TEXT" && templateData.headerMedia) {
                        // Handle media headers (image, video, document) with robust logic
                        const filename = templateData.templateName || `template_${nodeId}`;
                        components.push({
                            type: "HEADER",
                            format: templateData.headerType.toUpperCase(),
                            example: {
                                header_handle: [] // Placeholder for the backend to handle
                            }
                        });
    
                        let file;
                        if (typeof templateData.headerMedia === 'string') {
                            try {
                                const response = await fetch(templateData.headerMedia);
                                const blob = await response.blob();
                                file = new File([blob], filename, { type: blob.type });
                            } catch (error) {
                                console.error('Error fetching file from URL:', error);
                                continue;
                            }
                        } else if (templateData.headerMedia?.file) {
                            // Handle file object directly
                            file = templateData.headerMedia.file;
                        } else if (templateData.headerMedia?.fileInfo && templateData.headerMedia?.base64) {
                            // Reconstruct file from localStorage data
                            try {
                                const mimeMatch = templateData.headerMedia.base64.match(/^data:([^;]+);base64,/);
                                const mimeType = mimeMatch?.[1] || templateData.headerMedia.fileInfo.type || 'image/jpeg';
                                const base64Data = templateData.headerMedia.base64.replace(/^data:([^;]+);base64,/, '');
                                const byteCharacters = atob(base64Data);
                                const byteArrays = [];
                                for (let i = 0; i < byteCharacters.length; i += 512) {
                                    const slice = byteCharacters.slice(i, i + 512);
                                    const byteNumbers = new Array(slice.length);
                                    for (let j = 0; j < slice.length; j++) {
                                        byteNumbers[j] = slice.charCodeAt(j);
                                    }
                                    byteArrays.push(new Uint8Array(byteNumbers));
                                }
                                const blob = new Blob(byteArrays, { type: mimeType });
                                file = new File([blob], templateData.headerMedia.fileInfo.name || filename, { 
                                    type: mimeType,
                                    lastModified: templateData.headerMedia.fileInfo.lastModified || Date.now()
                                });
                            } catch (error) {
                                console.error('Error reconstructing file from localStorage data:', error);
                                continue;
                            }
                        } else if (templateData.headerMedia?.base64) {
                            // Reconstruct file from just base64 data
                            try {
                                const mimeMatch = templateData.headerMedia.base64.match(/^data:([^;]+);base64,/);
                                const mimeType = mimeMatch?.[1] || 'image/jpeg';
                                const base64Data = templateData.headerMedia.base64.replace(/^data:([^;]+);base64,/, '');
                                const byteCharacters = atob(base64Data);
                                const byteArrays = [];
                                for (let i = 0; i < byteCharacters.length; i += 512) {
                                    const slice = byteCharacters.slice(i, i + 512);
                                    const byteNumbers = new Array(slice.length);
                                    for (let j = 0; j < slice.length; j++) {
                                        byteNumbers[j] = slice.charCodeAt(j);
                                    }
                                    byteArrays.push(new Uint8Array(byteNumbers));
                                }
                                const blob = new Blob(byteArrays, { type: mimeType });
                                file = new File([blob], filename, { type: mimeType });
                            } catch (error) {
                                console.error('Error converting base64 to file:', error);
                                continue;
                            }
                        }
    
                        // If a file was successfully created, add it to the lists for upload
                        if (file) {
                            files.push(file);
                            fileNames.push(filename);
                        }
                    }
                }
    
                // Format the body text according to WhatsApp guidelines
                const formatWhatsappText = (text) => {
                  if (!text) return '';
                  let formattedText = text;
                  formattedText = formattedText.replace(/<strong\s*?>(.*?)<\/strong>/gi, '*$1*');
                  formattedText = formattedText.replace(/<b\s*?>(.*?)<\/b>/gi, '*$1*');
                  formattedText = formattedText.replace(/<em\s*?>(.*?)<\/em>/gi, '_$1_');
                  formattedText = formattedText.replace(/<i\s*?>(.*?)<\/i>/gi, '_$1_');
                  formattedText = formattedText.replace(/<strike\s*?>(.*?)<\/strike>/gi, '~$1~');
                  formattedText = formattedText.replace(/<del\s*?>(.*?)<\/del>/gi, '~$1~');
                  formattedText = formattedText.replace(/<code\s*?>(.*?)<\/code>/gi, '`$1`');
                  formattedText = formattedText.replace(/<br\s*?\/?>/gi, '\n');
                  formattedText = formattedText.replace(/<\/p>/gi, '\n');
                  formattedText = formattedText.replace(/<\/div>/gi, '\n');
                  formattedText = formattedText.replace(/<[^>]+>/g, '');
                  formattedText = formattedText.replace(/&nbsp;/g, ' ');
                  formattedText = formattedText.replace(/&amp;/g, '&');
                  formattedText = formattedText.replace(/&lt;/g, '<');
                  formattedText = formattedText.replace(/&gt;/g, '>');
                  formattedText = formattedText.replace(/\n{3,}/g, '\n\n');
                  return formattedText.trim();
                };
    
                components.push(...(templateData.body ? [{ type: "BODY", text: formatWhatsappText(templateData.body) || "" }] : []));
    
                if (templateData.buttons?.length > 0) {
                    const buttonComponents = templateData.buttons.map(button => {
                        console.log("button=======", button);
                
                       
                        let action;
                        if (button.typeOfAction) {
                            // If the button has typeOfAction (from the form), we trust it.
                            action = button.typeOfAction;
                        } else {
                            // Otherwise, we deduce the action from the API's `type` property.
                            switch (button.type) {
                                case 'URL':
                                    // We must still check if it's a site visit URL.
                                    action = button.url && button.url.toLowerCase().includes('/site-visit')
                                        ? 'site_visit'
                                        : 'visit_website';
                                    break;
                                case 'PHONE_NUMBER':
                                    action = 'call_phone_number';
                                    break;
                                case 'COPY_CODE':
                                    action = 'copy_offer_code';
                                    break;
                                default:
                                    // For 'QUICK_REPLY', 'property_config', etc.
                                    action = 'custom';
                                    break;
                            }
                        }
                        // ===================== END OF FIX ==============================
                
                
                        // Step 2: Use the reliable `action` variable in the switch.
                        // This switch is now guaranteed to work for all buttons.
                        switch (action) {
                            case 'site_visit':
                            case 'visit_website':
                                return {
                                    type: 'URL',
                                    text: button.buttonText || button.label || button.text || '',
                                    url: button.websiteURL || button.url || ''
                                };
                
                            case 'call_phone_number':
                                return {
                                    type: 'PHONE_NUMBER',
                                    text: button.buttonText || button.label || button.text || '',
                                    phone_number: button.country_code && (button.phoneNumber || button.phone_number)
                                        ? `+${button.country_code}${button.phoneNumber || button.phone_number}`
                                        : (button.phoneNumber || button.phone_number || '')
                                };
                
                            case 'copy_offer_code':
                            case 'copy_code':
                                return {
                                    type: 'COPY_CODE',
                                    text: button.buttonText || button.label || button.text || '',
                                    offer_code: button.offerCode || button.offer_code || ''
                                };
                
                            case 'property_config':
                            case 'custom':
                            case 'marketing_opt_out':
                            default:
                                return {
                                    type: 'QUICK_REPLY',
                                    text: button.buttonText || button.label || button.text || ''
                                };
                        }
                    });
                    
                    components.push({ type: "BUTTONS", buttons: buttonComponents });
                }
                const templateType = getTemplateType(nodeId, templateData);
                const personaIds = Array.isArray(templateData.leadType) && templateData.leadType.length > 0 ?
                    templateData.leadType.map(id => typeof id === 'string' ? parseInt(id, 10) : id).filter(id => !isNaN(id)) :
                    [1, 2, 3, 4, 5];
                const propertyConfigIds = [];
                if (templateData.buttons && templateData.buttons.length > 0) {
                    templateData.buttons.forEach(button => {
                        if (button.typeOfAction === 'property_config' && button.configId) {
                            propertyConfigIds.push(button.configId);
                        }
                    });
                }
                const isFestivalTemplate = templateType === 'FESTIVAL_TEMPLATE' || templateType === 'FESTIVAL_OFFER_TEMPLATE';
                const friendlyName = isFestivalTemplate ? templateData.templateName : (templateData.templateName || `Template ${nodeId}`);
    
                console.log('saveWorkflow - templateData.leadStatus for', nodeId, ':', templateData.leadStatus);
                const finalLeadStatus = Array.isArray(templateData.leadStatus) ? templateData.leadStatus : (templateData.leadStatus ? [templateData.leadStatus] : null);
                console.log('saveWorkflow - finalLeadStatus for', nodeId, ':', finalLeadStatus);
                templates.push({
                    customer_id: project.customerId,
                    project_id: project.projectId,
                    persona: personaIds,
                    lead_status: finalLeadStatus, // Include lead status
                    friendly_name: friendlyName,
                    conditions: templateData.conditionString || '',
                    components: components,
                    template_type: templateType,
                    ...(templateType !== 'REPLY_NODE_TEMPLATE' && {
                        send_datetime: requiresSchedulingCallback() ?
                            new Date(scheduledDateTime).toISOString().replace('T', ' ').slice(0, 19) :
                            new Date().toISOString().replace('T', ' ').slice(0, 19)
                    }),
                    order: templates.length,
                    property_config_ids: propertyConfigIds.length > 0 ? propertyConfigIds : (templateData.propertyConfigIds || [])
                });
            }
    
            allEdges.forEach((edge) => {
                const sourceTemplate = allTemplates.find(t => t.nodeId === edge.source);
                const targetTemplate = allTemplates.find(t => t.nodeId === edge.target);
                if (sourceTemplate && targetTemplate) {
                    const buttonIndex = edge.sourceHandle?.startsWith('button-') ? parseInt(edge.sourceHandle.split('-')[1]) : -1;
                    const button = buttonIndex >= 0 && sourceTemplate.templateData?.buttons?.[buttonIndex];
                    const replyMessage = button?.buttonText || "I'll get back to you.";
                    const propertyConfigId = (button?.typeOfAction === 'property_config' && button?.configId) ? button.configId : null;
                    replies.push({
                        source_template_friendly_name: sourceTemplate.templateData.templateName || `Template ${sourceTemplate.nodeId}`,
                        reply_message: replyMessage,
                        destination_template_friendly_name: targetTemplate.templateData.templateName || `Template ${targetTemplate.nodeId}`,
                        property_config_id: propertyConfigId,
                        site_visit_template: targetTemplate.templateType === 'SITE_VISIT_TEMPLATE'
                    });
                }
            });
    
            const campaignData = {
                templates: templates,
                replies: replies,
                customer_id: project.customerId,
                project_id: project.projectId,
            };
    
            const formData = new FormData();
            formData.append('campaign_data', JSON.stringify(campaignData));
    
            if (files.length > 0) {
                files.forEach((file, index) => {
                    if (file instanceof File && file.name && file.size > 0) {
                        formData.append('files', file);
                        formData.append('file_names', fileNames[index]);
                    } else {
                        console.error(`Invalid file at index ${index}:`, file);
                    }
                });
            } else {
                formData.append('files', new Blob([], { type: "application/octet-stream" }));
                formData.append('file_names', '');
            }
    
            try {
                const response = await axiosInstance.post('/customer/whatsapp/drip-campaign', formData, {});
                
                const currentLeadType = getCurrentLeadType(leadType);
                clearCampaignData(currentLeadType);
                clearAutoSavedCampaignState(currentLeadType);
    
                nodeIdCounter = 1;
    
                toast.success("Campaign saved successfully!");
                setRefreshTemplates(prev => prev + 1);
                
                if (response && response.templates && response.templates.length > 0) {
                    const newWorkflow = {
                        templates: response.templates,
                        replies: response.replies || [],
                    };
                    setTimeout(() => {
                        handleViewWorkflow(newWorkflow, true);
                    }, 500);
                }
            } catch (error) {
                console.error("Error saving campaign:", error);
                if (error.response?.data?.detail) {
                    toast.error(`API error: ${typeof error.response.data.detail === 'string' ?
                        error.response.data.detail :
                        JSON.stringify(error.response.data.detail)}`);
                } else {
                    toast.error("Failed to save campaign");
                }
            } finally {
                setIsLoading(false);
            }
        } catch (err) {
            console.error("Unexpected error in saveWorkflow:", err);
            toast.error("Failed to save campaign due to an unexpected error");
            setIsLoading(false);
        }
    }, [project, navigate, loadAllTemplatesFromLocalStorageCallback, loadEdgesFromLocalStorageCallback, getTemplateType, requiresSchedulingCallback, scheduledDateTime]);

    const handleViewWorkflow = useCallback((workflow, isEditing = false) => {
        
        setIsViewMode(!isEditing);
       

        // Generate a unique prefix for the copied workflow
        const copyPrefix = `copy-${Date.now()}-`;

        // Create a mapping of old IDs to new IDs
        const idMapping = new Map();

        // First pass: Create new IDs for all templates
        workflow.templates.forEach(template => {
            const newId = `${copyPrefix}${template.customerWhatsappTemplateId}`;
            idMapping.set(template.customerWhatsappTemplateId, newId);
        });

        // Group templates by their flow
        const flowGroups = new Map();
        workflow.templates.forEach(template => {
            const isReplyTemplate = template.templateType === 'REPLY_NODE_TEMPLATE';
            const isConnected = workflow.replies.some(reply =>
                reply.destinationTemplate.customerWhatsappTemplateId === template.customerWhatsappTemplateId
            );

            if (!isReplyTemplate && !isConnected) {
                // This is a starting template
                flowGroups.set(template.customerWhatsappTemplateId, [template]);
            }
        });

        // Add connected templates to their respective flows
        workflow.replies.forEach(reply => {
            const sourceId = reply.sourceTemplate.customerWhatsappTemplateId;
            const destId = reply.destinationTemplate.customerWhatsappTemplateId;

            // Find which flow this connection belongs to
            for (const [startId, templates] of flowGroups.entries()) {
                if (templates.some(t => t.customerWhatsappTemplateId === sourceId)) {
                    // Add destination template to this flow
                    const destTemplate = workflow.templates.find(t => t.customerWhatsappTemplateId === destId);
                    if (destTemplate) {
                        templates.push(destTemplate);
                    }
                    break;
                }
            }
        });

        // Calculate the position for the new workflow - center it
        const existingNodes = nodesRef.current;
        
        // Get the viewport dimensions to center the workflow
        const viewportWidth = window.innerWidth || 1200;
        const viewportHeight = window.innerHeight || 800;
        
        // Calculate center position
        const centerX = viewportWidth / 2 - 200; // Offset for node width
        const centerY = viewportHeight / 2 - 100; // Offset for node height
        
        // Create nodes with centered positioning
        const newNodes = [];
        const ySpacing = 200;
        const xSpacing = 400;

        flowGroups.forEach((templates, startId) => {
            // Get the initial template's send date for this flow
            const initialTemplate = templates[0];
            const inheritedSendDatetime = initialTemplate?.sendDatetime;

            templates.forEach((template, index) => {
                const position = {
                    x: centerX + (index * xSpacing),
                    y: centerY + (index * ySpacing)
                };

                // Extract button information
                const buttons = template.components
                    .find(c => c.type === 'BUTTONS')?.buttons || [];

                // Get header and body text
                const headerComponent = template.components.find(c => c.type === 'HEADER');
                const bodyComponent = template.components.find(c => c.type === 'BODY');

                const headerText = headerComponent?.text || '';
                const bodyText = bodyComponent?.text || '';

                const newId = idMapping.get(template.customerWhatsappTemplateId);

                const templateData = {
                    id: newId,
                    type: template.templateType === 'REPLY_NODE_TEMPLATE' ? 'replyNode' : 'initialMessageNode',
                    data: {
                        ...template,
                        // For reply templates, use the initial template's send date
                        sendDatetime: template.templateType === 'REPLY_NODE_TEMPLATE' ? inheritedSendDatetime : template.sendDatetime,
                        label: template.friendlyName,
                        headerType: headerComponent?.format?.toLowerCase() || 'text',
                        headerText: headerText,
                        body: bodyText,
                        buttons: buttons.map(button => ({
                            buttonText: button.text,
                            typeOfAction: button.type.toLowerCase(),
                            websiteURL: button.url,
                            phoneNumber: button.phone_number
                        })),
                        onCreateTemplate: onCreateTemplateRef.current,
                        onAddReplyNode: onAddReplyNodeRef.current,
                        isViewOnly: !isEditing && !template.isPublished, // Allow editing published templates even in view mode
                        isPublished: template.isPublished,
                        project
                    },
                    position
                };
                newNodes.push(templateData);
            });
        });

        // Create edges based on connections with new IDs
        const newEdges = (workflow.replies || []).map((conn) => {
            if (!conn.sourceTemplate || !conn.destinationTemplate) {
                return null;
            }

            const sourceId = idMapping.get(conn.sourceTemplate.customerWhatsappTemplateId);
            const targetId = idMapping.get(conn.destinationTemplate.customerWhatsappTemplateId);

            // Find the source template to get the button index
            const sourceTemplate = workflow.templates.find(t => t.customerWhatsappTemplateId === conn.sourceTemplate.customerWhatsappTemplateId);
            const buttons = sourceTemplate?.components?.find(c => c.type === 'BUTTONS')?.buttons || [];

            // Find the button index based on the reply message
            let sourceHandle = undefined;
            const buttonIndex = buttons.findIndex(button => button.text === conn.replyMessage);
            if (buttonIndex !== -1) {
                sourceHandle = `button-${buttonIndex}`;
            }

            return {
                id: `edge-${sourceId}-${targetId}`,
                source: sourceId,
                target: targetId,
                sourceHandle,
                type: 'default',
                animated: true,
                style: {
                    stroke: '#2563eb',
                    strokeWidth: 2
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#2563eb'
                },
                data: {
                    replyMessage: conn.replyMessage
                }
            };
        }).filter(Boolean);

        // Set the new nodes and edges
        setNodes(newNodes);
        setEdges(newEdges);

        // Set template form data to indicate we're viewing/editing a saved template
        if (workflow.templates && workflow.templates.length > 0) {
            const mainTemplate = workflow.templates[0]; // Use the first template as the main one
            setTemplateFormData({
                ...mainTemplate,
                isViewingSavedTemplate: isEditing, // Only set to true when editing
                templateId: mainTemplate.customerWhatsappTemplateId,
                isUpdating: false
            });
        } else if (!isEditing) {
            // Reset template form data when not editing and no templates
            setTemplateFormData(null);
        }

        // Force a re-render of the flow and center the view
        setTimeout(() => {
            if (reactFlowInstance) {
                // Center the view on the new workflow
                reactFlowInstance.fitView({ 
                    padding: 0.3,
                    includeHiddenNodes: false,
                    minZoom: 0.5,
                    maxZoom: 1.2
                });
            }
        }, 100);
    }, [setNodes, setEdges, reactFlowInstance, isViewMode]);

    return (
        <div className="flex h-screen bg-gray-100" ref={reactFlowWrapper}>
            <PreviousTemplates project={project} onViewWorkflow={handleViewWorkflow} refreshTrigger={refreshTemplates} />
            <div className="flex-1 flex flex-col">
                <div className="bg-gray-100 py-4 px-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/communications')}
                            className="min-w-[32px] min-h-[32px] flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                            aria-label="Back"
                            style={{ border: 'none', background: 'none', padding: 0, marginRight: '8px' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-gray-700">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                            </svg>
                        </button>
                        <h2 className="text-xl font-semibold text-gray-800 flex items-center">

                            {getCurrentLeadType(leadType).charAt(0).toUpperCase() + getCurrentLeadType(leadType).slice(1).replace('_', ' ')} Campaign
                        </h2>
                        {/* <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                            isWorkflowLimitReached 
                                ? 'bg-red-50 text-red-700' 
                                : 'bg-blue-50 text-flamingo-500'
                        }`}>
                            Workflows: {existingWorkflows}/4 {isWorkflowLimitReached && '(Limit Reached)'}
                        </div> */}
                    </div>
                    <div className="flex gap-3">
                        
                       
                        {/* Show different buttons based on edit mode */}
                        <OutlineButton
                            onClick={resetFlow}
                            disabled={isLoading}
                            className="font-medium gap-1.5"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                            Reset
                        </OutlineButton>
                        <button
                            onClick={createNewWorkflow}
                            disabled={isLoading}
                            className="p-2 w-full rounded-md border border-gray-200 bg-white hover:bg-gray-100 transition-colors flex items-center justify-center disabled:opacity-50"
                            title="Create new workflow"
                            type="button"
                        >
                            <Plus className="w-5 h-5 text-flamingo-500" /> <span className='text-flamingo-500 text-sm font-medium px-2'>Create New</span>
                        </button>
                        
                        <div className="flex flex-col gap-2">
                          
                            <PrimaryButton
                                onClick={() => saveWorkflow().catch(e => console.error("Error in saveWorkflow:", e))}
                                disabled={isLoading || isViewMode || showTemplateForm || templateFormData?.isViewingSavedTemplate}
                                className={`w-full py-2 px-4 gap-1.5`}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Saving...
                                    </>
                                ) : showTemplateForm ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                                        </svg>
                                        {isUpdatingTemplate ? 'Updating Template...' : 'Close Template to Save'}
                                    </>
                                ) : (
                                    <>
                                        Save Campaign
                                    </>
                                )}
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
                <div className="flex-1 relative border rounded-lg shadow-md p-4 m-4 bg-white">
                    {isInitializing && (
                        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flamingo-500 mx-auto mb-4"></div>
                                <p className="text-gray-600 font-medium">Loading workflow...</p>
                            </div>
                        </div>
                    )}
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onNodeClick={onNodeClick}
                        onNodesDelete={onNodesDelete}
                        onEdgeClick={onEdgeClick}
                        onInit={setReactFlowInstance}
                        defaultEdgeOptions={defaultEdgeOptions}
                        connectionLineStyle={{
                            stroke: '#2563eb',
                            strokeWidth: 3,
                            strokeDasharray: '5,5'
                        }}
                        connectionMode="loose"
                        connectionLineType={ConnectionLineType.SmoothStep}
                        fitView
                        fitViewOptions={{
                            padding: 0.5,
                            maxZoom: 1.2,
                            minZoom: 0.5,
                            initialZoom: 0.8
                        }}
                        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
                        nodeTypes={nodeTypes}
                        deleteKeyCode={['Backspace', 'Delete']}
                        multiSelectionKeyCode="Control"
                        selectionKeyCode="Shift"
                    >
                        <Controls />
                        <Background variant="dots" gap={12} size={1} />
                        <Panel position="top-left">
                            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
                                <div
                                    className="bg-gradient-to-r from-flamingo-500 to-flamingo-500 py-2 px-3 flex items-center justify-between cursor-pointer"
                                    onClick={() => setShowGuide((prev) => !prev)}
                                >
                                    <h3 className="text-sm font-medium text-white flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                                        </svg>
                                        Quick Guide
                                    </h3>
                                    <span className="ml-2">
                                        {showGuide ? (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        ) : (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        )}
                                    </span>
                                </div>
                                {showGuide && (
                                    <div className="p-3">
                                        <ul className="text-xs text-gray-600 space-y-2">
                                            <li className="flex items-start">
                                                <span className="bg-flmaingo-200 text-flmaingo-600 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">1</span>
                                                <span>Create a template by clicking <span className="text-flmaingo-600 font-medium">Create Template</span></span>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="bg-flmaingo-100 text-flmaingo-600 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">2</span>
                                                <span>Add replies by clicking <span className="text-flmaingo-600 font-medium">Add Reply</span></span>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="bg-flmaingo-100 text-flmaingo-600 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">3</span>
                                                <span>Use drag and drop to connect message nodes</span>
                                            </li>
                                            <li className="flex items-start">
                                                <span className="bg-flmaingo-100 text-flmaingo-600 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">4</span>
                                                <span>Click on any connection arrow to delete it</span>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </Panel>
                    </ReactFlow>
                </div>
            </div>
            <Drawer open={open} onClose={handleClose} size="xl">
                
                <Drawer.Overlay>
                    <Drawer.Panel className="p-6 w-[45%] overflow-y-auto">
                        <div className="mb-6 flex items-center justify-between">
                            <h5 className=" text-xl font-semibold text-blue-gray-900">
                                Template Editor
                            </h5>
                            <CloseButton onClick={handleClose} />
                        </div>
                       
                        {showTemplateForm && selectedNode && (
                            <Template
                                initialData={templateFormData}
                                onChange={handleTemplateUpdate}
                                onUpdate={handleTemplateUpdate}
                                onClose={handleClose}
                                onDelete={handleTemplateDelete}
                                nodeId={selectedNode.id}
                                isViewingSavedTemplate={Boolean(templateFormData?.customerWhatsappTemplateId)}
                                isViewMode={isViewMode}
                                templateId={templateFormData?.customerWhatsappTemplateId}
                                isUpdatingTemplate={isUpdatingTemplate}
                                isPublishedTemplate={Boolean(templateFormData?.isPublished)}
                            />
                        )}
                    </Drawer.Panel>
                </Drawer.Overlay>
            </Drawer>
            <ConfirmationModal
                isOpen={showResetConfirmation}
                onConfirm={handleConfirmReset}
                onCancel={handleCancelReset}
                message="Are you sure you want to reset the entire workflow? This will delete all nodes and templates."
            />
            
           
        </div>
    );
}

export default CreateWorkflow;